import { TabContext, TabPanel } from '@mui/lab';
import {
  Button,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Tab,
  Theme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { Formik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { useRecoilStateLoadable } from 'recoil';
import * as Yup from 'yup';
import { FormikTextField } from '../../common/formik-material-ui';
import '../../common/formik-material-ui/uniqueProperty';
import { loadableEnum } from '../../common/useMakePHPRequest';
import { IUserAtom, user } from '../../recoil/atoms/user';
import { getApiMutation } from '../../services/rest/getApiMutation';
import { endpointEnum } from '../../services/rest/makeAPIRequest';
import {
  getErrorTabIndex,
  isErrorInTab,
} from '../components/modals/DevicesAndRoutingModal';
import { WizardModal } from '../components/modals/WizardModal';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';

const useStyles = makeStyles((theme: Theme) => ({
  tabs: {},
  tab: {
    width: '100%',
    maxWidth: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
      justifyContent: 'flex-start',
    },
    '&[tabindex="0"]': {
      backgroundColor: theme.palette.divider,
    },
  },
  warningIcon: {
    color: 'orange',
  },
  reset: {
    margin: 0,
    padding: 0,
  },
  header: {
    color: 'var(--primary)', // pull color from primary
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: 0,
  },
  delete: {
    padding: 0, // shrink delete icon on action button
  },
  editButton: {
    maxWidth: '155px',
    maxHeight: '50px',
    minWidth: '155px',
    minHeight: '50px',
  },
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  fileUploadLabel: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
    height: 'min-content',
  },
  addButton: {
    marginTop: theme.spacing(2), // align with fields
    marginBottom: theme.spacing(2), // align with fields
  },
  table: {
    marginTop: theme.spacing(2), // align with fields
    marginBottom: theme.spacing(2), // align with fields
  },
  deleteIconButton: {
    color: 'red',
  },
  deleteIconButtonDisabled: {
    color: 'grey',
  },
  button: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    '& .MuiButton-label': {
      padding: 2,
    },
  },
  audio: {
    '&:focus': {
      outline: 0,
    },
  },
  modal: {
    paddingBottom: 0,
  },
  defaultAction: {
    marginTop: theme.spacing(2),
  },
  tabIcon: {
    marginRight: 15,
  },
  surveyContainer: {
    display: 'flex',
    flexDirection: 'column',
    margin: '0px 68px',
  },
  surveyQuestionContainer: {
    marginBottom: '24px',
  },
  surveyQuestionTitle: {
    fontSize: '16px',
  },
  surveyQuestionTitleContainer: {},
  surveyRadioButtonGroupContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    margin: '8px 0px',
  },
  surveyRadioButtonContainer: {
    padding: '0px 12px',
    width: '280px',
    height: '28px',
  },
  surveyRadioButton: {},
  surveyTextContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  surveyText: {
    marginTop: '12px',
    width: '100%',
    resize: 'none',
  },
  stayButton: {
    color:
      theme.palette.mode === 'light' ? 'rgb(104,71,255)' : 'rgb(154,121,255)',
    border:
      theme.palette.mode === 'light'
        ? '2px solid rgb(104,71,255)'
        : '2px solid rgb(154,121,255)',
    backgroundColor: 'transparent',
    fontWeight: 600,
    boxShadow: 'none',
    '&:hover': {
      color:
        theme.palette.mode === 'light' ? 'rgb(124,71,255)' : 'rgb(174,131,255)',
      border:
        theme.palette.mode === 'light'
          ? '2px solid rgb(124,71,255)'
          : '2px solid rgb(174,131,255)',
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
    '&:focus': {
      color:
        theme.palette.mode === 'light' ? 'rgb(134,71,255)' : 'rgb(184,141,255)',
      border:
        theme.palette.mode === 'light'
          ? '2px solid rgb(134,71,255)'
          : '2px solid rgb(184,141,255)',
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
    '&:disabled': {
      backgroundColor: 'transparent',
      color:
        theme.palette.mode === 'light' ? 'rgb(104,71,255)' : 'rgb(134,101,255)',
      border:
        theme.palette.mode === 'light'
          ? '2px solid rgb(104,71,255)'
          : '2px solid rgb(134,101,255)',
    },
  },
  continueButton: {
    color: 'white',
    backgroundColor: '#1bc5bd',
    boxShadow: 'none',
    fontWeight: 500,
    '&:hover': {
      color: 'white',
      backgroundColor: '#1bc5bd',
      boxShadow: 'none',
    },
    '&:focus': {
      color: 'white',
      backgroundColor: '#1bc5bd',
      boxShadow: 'none',
    },
  },
  surveySubtext: {
    fontSize: '14px',
    fontWeight: 700,
  },
}));

// Fisher-Yates shuffle algorithm
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const cancellationReasonOptions = [
  { value: 'closedBusiness', label: 'Closed Business' },
  { value: 'mobileApp', label: 'Mobile App' },
  { value: 'technicalIssues', label: 'Technical Issues' },
  { value: 'poorSupport', label: 'Poor Support' },
  { value: 'tooExpensive', label: 'Too Expensive' },
  { value: 'other', label: 'Other' },
];

export function CancellationModal({
  show,
  onHide,
  id,
  lastInvoiceUntilDatetime,
}) {
  const classes = useStyles();
  const [userAtom, setUserAtom] = useRecoilStateLoadable(user);
  const userAtomContents = userAtom.contents as IUserAtom;
  const mutation = useMutation(getApiMutation(userAtomContents, setUserAtom));
  const [submitState, setSubmitState] = useState(loadableEnum.EMPTY);
  const [page, setPage] = useState(0);
  const [tabIndex, setTabIndex] = useState(0);
  // eslint-disable-next-line

  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const formikRef = useRef<any>(null);

  // Shuffle cancellation reasons once on mount
  const shuffledCancellationReasons = useMemo(
    () => shuffleArray(cancellationReasonOptions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [show]
  );

  const initialValues = {
    cancellationReason: '',
    alternateService: '',
    retainBusinessComment: '',
  };

  const surveySchema = useMemo(
    () =>
      Yup.object({
        cancellationReason: Yup.string().required('Required'),
        alternateService: Yup.string().required('Required'),
        retainBusinessComment: Yup.string()
          .max(1000, '1000 max char limit')
          .min(25, '25 min char limit')
          .required('Required'),
      }),
    []
  );

  const tabToFields = [
    ['cancellationReason', 'alternateService', 'retainBusinessComment'],
  ];

  useEffect(() => {
    if (formikRef?.current) {
      formikRef.current.resetForm({});
    }
    setPage(0);
  }, [show]);

  return (
    <Paper elevation={0} square={true}>
      <Formik
        enableReinitialize={true}
        validationSchema={surveySchema}
        onSubmit={async (values, actions) => {
          setSubmitState(loadableEnum.LOADING);
          //   console.log(_result, 'result updating ');
          const data = {
            call: 'submitCancellationSurvey',
            apikey: userAtomContents?.apikey,
            businessid: userAtomContents.businessid,
            userid: userAtomContents.userid,
            cancelreason: values.cancellationReason,
            alternateservice: values.alternateService,
            retainbusinesscomment: values.retainBusinessComment,
            email: userAtomContents.user.email,
          };
          await mutation.mutateAsync({
            endpoint: endpointEnum.SUBMIT_CANCELLATION_SURVEY,
            payload: data,
          });

          // const requestConfig: AxiosRequestConfig = {
          //   method: 'get',
          //   url: `${process.env.VITE_APP_CDK_DOMAIN}/itn/removeBusiness?businessid=${userAtomContents.businessid}&apikey=${userAtomContents.apikey}`,
          //   withCredentials: true,
          // };

          // // make ajax request
          // await Axios(requestConfig);
          enqueueSnackbarWithStyles({
            message: `Account cancellation submitted.`,
            alertProps: { severity: 'info' },
          });
          setSubmitState(loadableEnum.HAS_VALUE);
          onHide();
        }}
        initialValues={initialValues}
        innerRef={formikRef}
        // validateOnChange={false}
        // validateOnBlur={false}
      >
        {(formik) => {
          const submitCancellationRequest = async () => {
            setSubmitState(loadableEnum.LOADING);
            //   console.log(_result, 'result updating ');
            const data = {
              call: 'submitCancellationSurvey',
              apikey: userAtomContents?.apikey,
              businessid: userAtomContents.businessid,
              userid: userAtomContents.userid,
              cancelreason: formik.values.cancellationReason,
              alternateservice: formik.values.alternateService,
              retainbusinesscomment: formik.values.retainBusinessComment,
              email: userAtomContents.user.email,
            };
            await mutation.mutateAsync({
              endpoint: endpointEnum.SUBMIT_CANCELLATION_SURVEY,
              payload: data,
            });

            // // make ajax request
            // await Axios(requestConfig);
            enqueueSnackbarWithStyles({
              message: `Account cancellation submitted.`,
              alertProps: { severity: 'info' },
            });
            setSubmitState(loadableEnum.HAS_VALUE);
            onHide();
          };
          return (
            <TabContext value={String(tabIndex)}>
              <WizardModal
                title={'Cancel Account'}
                hideTabs={true}
                disableSubmit={!formik.isValid}
                isLoading={submitState === loadableEnum.LOADING}
                navigation={
                  <div>
                    <Tab
                      label={'Cancel Account'}
                      disabled={true}
                      className={clsx(classes.tab)}
                    />
                  </div>
                }
                dialogActions={
                  <>
                    <Button
                      className={clsx(classes.stayButton, classes.button)}
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        onHide();
                        setPage(0);
                      }}
                    >
                      Stay with Kixie
                    </Button>
                    <Button
                      className={clsx(classes.button, classes.continueButton)}
                      variant="contained"
                      color="primary"
                      disabled={
                        !formik.values.alternateService ||
                        !formik.values.cancellationReason ||
                        !formik.values.retainBusinessComment ||
                        formik.values.retainBusinessComment?.length < 25 ||
                        formik.values.retainBusinessComment?.length > 1000
                      }
                      //   disabled={formik.isSubmitting || !formik.dirty}
                      onClick={async (event) => {
                        if (page) {
                          // trigger show errors if errors exists
                          await submitCancellationRequest();

                          // switch tabs and suppress submit if error not in current tab (submit will block if errors detected)
                          if (
                            !formik.isValid && // if form invalid
                            !isErrorInTab(tabIndex, formik.errors, tabToFields) // and error not in this tab
                          ) {
                            const errorTabIndex = getErrorTabIndex(
                              formik.errors,
                              tabToFields
                            );
                            /**
                             * switch to tab containing error
                             *
                             * since form validation is in flight and makes page rendering updates
                             * set state change in separate event loop cycle (tick) since it also makes page rendering updates
                             * putting state changes in a separate tick will prevent coliding page rendering updates
                             *
                             * @see https://blog.sessionstack.com/how-javascript-works-event-loop-and-the-rise-of-async-programming-5-ways-to-better-coding-with-2f077c4438b5
                             */
                            setTimeout(() => setTabIndex(errorTabIndex ?? 0));
                          }
                          onHide();
                          setPage(0);
                        } else {
                          setPage(1);
                        }
                      }}
                    >
                      {page ? 'I accept the cancellation terms' : 'Continue'}
                    </Button>
                  </>
                }
                size="md"
                show={show}
                onHide={() => {
                  onHide();
                }}
                onSubmit={async () => {
                  submitCancellationRequest();
                }}
              >
                <TabPanel value="0">
                  {page ? (
                    <div
                      style={{ margin: '0px 72px' }}
                      className={classes.surveyContainer}
                    >
                      <p className={classes.surveySubtext}>
                        Kixie Cancellation Terms
                      </p>
                      <br />
                      <p className={classes.surveySubtext}>
                        {`Your account cancellation will occur within one business
                        day of ${lastInvoiceUntilDatetime}.`}
                      </p>
                      <br />
                      <p className={classes.surveySubtext}>
                        Upon cancellation, your phone numbers, call history,
                        call statistics and call recordings will be removed from
                        our platform and you will not have access to your
                        account. However, call recordings that have been pushed
                        to your CRM will continue to be available lifetime on
                        that CRM.
                      </p>
                      <br />
                      <p className={classes.surveySubtext}>
                        To reactivate a Kixie account in the future, canceled
                        accounts are required to pay quarterly/annually,
                        depending on the number of agents.
                      </p>
                      <br />
                      <p className={classes.surveySubtext}>
                        There are no pro-rated refunds or credits upon
                        cancellation, as per the{' '}
                        <a
                          rel="noreferrer"
                          target={'_blank'}
                          href={
                            'https://support.kixie.com/hc/en-us/articles/225116528-How-does-Kixie-s-billing-work'
                          }
                        >
                          Kixie Billing Policy
                        </a>{' '}
                        and{' '}
                        <a
                          rel="noreferrer"
                          target={'_blank'}
                          href={'https://www.kixie.com/terms/'}
                        >
                          Terms of Service
                        </a>
                        . Your account will hereby be canceled and all services
                        deactivated.
                      </p>
                    </div>
                  ) : (
                    <div className={classes.surveyContainer}>
                      <div className={classes.surveyQuestionContainer}>
                        <div className={classes.surveyQuestionTitleContainer}>
                          <h3 className={classes.surveyQuestionTitle}>
                            What is the primary reason you are canceling your
                            account?
                          </h3>
                        </div>
                        <div
                          className={classes.surveyRadioButtonGroupContainer}
                        >
                          <RadioGroup
                            aria-labelledby="radio-buttons-group-cancellation-reason"
                            name="cancellationReason"
                            defaultValue={''}
                            onChange={(e) => {
                              formik.setFieldValue(
                                'cancellationReason',
                                (e.target as any).value
                              );
                            }}
                          >
                            {shuffledCancellationReasons
                              .reduce((rows, option, index) => {
                                const rowIndex = Math.floor(index / 2);
                                if (!rows[rowIndex]) {
                                  rows[rowIndex] = [];
                                }
                                rows[rowIndex].push(option);
                                return rows;
                              }, [] as typeof shuffledCancellationReasons[])
                              .map((row, rowIndex) => (
                                <div className="row" key={rowIndex}>
                                  {row.map((option) => (
                                    <div
                                      key={option.value}
                                      className={
                                        classes.surveyRadioButtonContainer
                                      }
                                    >
                                      <FormControlLabel
                                        value={option.value}
                                        control={<Radio color="primary" />}
                                        label={option.label}
                                      />
                                    </div>
                                  ))}
                                </div>
                              ))}
                          </RadioGroup>
                        </div>
                      </div>
                      <div className={classes.surveyQuestionContainer}>
                        <div className={classes.surveyQuestionTitleContainer}>
                          <h3 className={classes.surveyQuestionTitle}>
                            What service will you be using instead?
                          </h3>
                        </div>
                        <div
                          className={classes.surveyRadioButtonGroupContainer}
                        >
                          <RadioGroup
                            aria-labelledby="radio-buttons-group-alternate-service"
                            name="alternateService"
                            defaultValue={''}
                            onChange={(e) => {
                              formik.setFieldValue(
                                'alternateService',
                                (e.target as any).value
                              );
                            }}
                          >
                            <div className="row">
                              <div
                                className={classes.surveyRadioButtonContainer}
                              >
                                <FormControlLabel
                                  value="Five9"
                                  control={<Radio color="primary" />}
                                  label="Five9"
                                />
                              </div>
                              <div
                                className={classes.surveyRadioButtonContainer}
                              >
                                <FormControlLabel
                                  value="PhoneBurner"
                                  control={<Radio color="primary" />}
                                  label="Phone Burner"
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div
                                className={classes.surveyRadioButtonContainer}
                              >
                                <FormControlLabel
                                  value="CRMDialer"
                                  control={<Radio color="primary" />}
                                  label="CRM Dialer"
                                />
                              </div>
                              <div
                                className={classes.surveyRadioButtonContainer}
                              >
                                <FormControlLabel
                                  value="Orum"
                                  control={<Radio color="primary" />}
                                  label="Orum"
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div
                                className={classes.surveyRadioButtonContainer}
                              >
                                <FormControlLabel
                                  value="AirCall"
                                  control={<Radio color="primary" />}
                                  label="AirCall"
                                />
                              </div>
                              <div
                                className={classes.surveyRadioButtonContainer}
                              >
                                <FormControlLabel
                                  value="Cell Phone"
                                  control={<Radio color="primary" />}
                                  label="Cell Phone"
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div
                                className={classes.surveyRadioButtonContainer}
                              >
                                <FormControlLabel
                                  value="JustCall"
                                  control={<Radio color="primary" />}
                                  label="JustCall"
                                />
                              </div>
                              <div
                                className={classes.surveyRadioButtonContainer}
                              >
                                <FormControlLabel
                                  value="Other"
                                  control={<Radio color="primary" />}
                                  label="Other"
                                />
                              </div>
                            </div>
                          </RadioGroup>
                        </div>
                      </div>
                      <div
                        className={classes.surveyQuestionContainer}
                        style={{ marginBottom: '8px' }}
                      >
                        <div className={classes.surveyQuestionTitleContainer}>
                          <h3 className={classes.surveyQuestionTitle}>
                            What could we have done to keep your business?
                          </h3>
                        </div>
                        <div className={classes.surveyTextContainer}>
                          <FormikTextField
                            className={classes.surveyText}
                            id="retain-business-comment"
                            label=""
                            name="retainBusinessComment"
                            formik={formik}
                            size="small"
                            variant="outlined"
                            rows={4}
                            inputProps={{ maxLength: 1000 }}
                            required
                            multiline={true}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </TabPanel>
              </WizardModal>
            </TabContext>
          );
        }}
      </Formik>
    </Paper>
  );
}
